import {
  PriceRangeSelector,
  PrimaryAtmosphereSelector,
  AmenitiesSelector,
  ReservationPolicySelector,
} from '../../components/VenueForm';
import { VenueFormErrorBoundary } from '../../components/VenueForm/VenueFormErrorBoundary';
import { WarningTriangleIcon } from '../../components/ui/icons';
import { FormSection } from '../../components/ui/FormSection';
import type { PriceRange, PrimaryAtmosphere, Amenity, ReservationPolicy } from '@wineryxplorer/shared';

interface VenueDetailsSectionProps {
  submitError: string;
  priceRange: PriceRange | undefined;
  onPriceRangeChange: (value: PriceRange | undefined) => void;
  primaryAtmosphere: PrimaryAtmosphere | undefined;
  onPrimaryAtmosphereChange: (value: PrimaryAtmosphere | undefined) => void;
  amenities: Amenity[];
  onAmenitiesChange: (value: Amenity[]) => void;
  reservationPolicy: ReservationPolicy;
  onReservationPolicyChange: (value: ReservationPolicy) => void;
  bookingUrl: string;
  onBookingUrlChange: (value: string) => void;
  errors: {
    priceRange?: string[];
    primaryAtmosphere?: string[];
    amenities?: string[];
  };
  isSaving: boolean;
}

export const VenueDetailsSection = ({
  submitError,
  priceRange,
  onPriceRangeChange,
  primaryAtmosphere,
  onPrimaryAtmosphereChange,
  amenities,
  onAmenitiesChange,
  reservationPolicy,
  onReservationPolicyChange,
  bookingUrl,
  onBookingUrlChange,
  errors,
  isSaving,
}: VenueDetailsSectionProps) => (
  <FormSection title="Venue Details">
    {submitError && (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start space-x-2">
          <WarningTriangleIcon className="h-5 w-5 text-red-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Submission Error</h3>
            <p className="text-sm text-red-700 mt-1">{submitError}</p>
          </div>
        </div>
      </div>
    )}

    <VenueFormErrorBoundary>
      <div className="space-y-6">
        <PriceRangeSelector
          value={priceRange}
          onChange={onPriceRangeChange}
          error={errors.priceRange}
          loading={isSaving}
          disabled={isSaving}
        />

        <PrimaryAtmosphereSelector
          value={primaryAtmosphere}
          onChange={onPrimaryAtmosphereChange}
          error={errors.primaryAtmosphere}
          loading={isSaving}
          disabled={isSaving}
        />

        <AmenitiesSelector
          value={amenities}
          onChange={onAmenitiesChange}
          error={errors.amenities}
          loading={isSaving}
          disabled={isSaving}
        />

        <ReservationPolicySelector
          value={reservationPolicy}
          onChange={onReservationPolicyChange}
          loading={isSaving}
          disabled={isSaving}
        />

        <div>
          <label htmlFor="bookingUrl" className="block text-sm font-medium text-gray-700 mb-1">
            Booking URL
          </label>
          <input
            type="url"
            id="bookingUrl"
            value={bookingUrl}
            onChange={(e) => onBookingUrlChange(e.target.value)}
            placeholder="https://example.com/reserve"
            disabled={isSaving}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Where visitors book a visit. Works with the reservation policy above, and is used as the
            booking link on tasting experiences that don&apos;t set their own.
          </p>
        </div>
      </div>
    </VenueFormErrorBoundary>
  </FormSection>
);
